import {get} from "lodash"

import {Tenant, User, Business, UnauthedBusiness, UserTeamAssignment} from "@irati/types"
import {store} from "@irati/store"
import isSamlUserHelper from "@irati/identity/helpers/user/isSamlUser"
import locale from "@irati/utils/locale"
import {CurrencyType} from "@irati/payments/enums"
import paymentProviderForCountryCode from "@irati/payments/helpers/paymentProviderForCountryCode"
import {PaymentProvider} from "@irati/payments/enums/PaymentV2"

import {isGlobalAdmin as isGlobalAdminHelper} from "@irati/identity/helpers/user/role"
import AuthService from "./streams/auth"
import UnauthedService from "./streams/unauthed"
import Auth from "./types/ReduxAuth"
import AuthApps from "./types/ReduxAuth/AuthApps"
import AuthUnits from "./types/AuthUnits"
import AuthUpsell from "./types/ReduxAuth/AuthUpsell"

const apps = (key = ""): AuthApps | boolean => {
  try {
    const auth = AuthService.get()

    return key.length ? get(auth.productPermissions, key, false) : auth.productPermissions
  } catch (e) {
    return false
  }
}

const userProducts = () => {
  const {perks, recognition, medical} = apps()
  return [
    {
      product: "perks",
      active: !!perks
    },
    {
      product: "recognition",
      active: !!recognition
    },
    {
      product: "medical",
      active: !!medical
    }
  ]
}

const user = (): User => {
  const auth = AuthService.get()

  return auth.user
}

const userTenant = (key = ""): Tenant => {
  const auth = AuthService.get()

  return key.length ? get(auth.userTenant, key, false) : auth.userTenant
}

const business = (): Business => {
  const auth = AuthService.get()

  return auth.business
}

const teamAssignments = (): UserTeamAssignment[] => {
  const auth = AuthService.get()

  return auth.userTeamAssignments
}

const dealPackage = (): DealPackage => {
  const auth = AuthService.get()

  return auth.dealPackage
}

const unAuthedBusiness = (): UnauthedBusiness => {
  const auth = UnauthedService.get()

  return auth.unauthedBusiness
}

const upsell = (): AuthUpsell => {
  const {auth}: {auth: Auth} = store.getState()

  return auth.upsell || {}
}

const userUnits = (): AuthUnits => {
  switch (userLocale()) {
    case "en-GB":
      return {distance: locale.units.miles}
    default:
      return {distance: locale.units.km}
  }
}

const featureFlag = (key: string): boolean => {
  const {auth}: {auth: Auth} = store.getState()

  return get(auth.featureFlags, key, false)
}

const userLocale = (): string => {
  // Forcing all users to en-GB language - inline with graph changes
  return "en-GB"
}

const userPaymentProvider = (): PaymentProvider => {
  return paymentProviderForCountryCode()
}

const defaultUserCurrency = (): string => {
  // This method should not be driving any business logic anymore - to be removed
  switch (userLocale()) {
    case "fr-FR":
      return CurrencyType.EUR
    case "en-AU":
      return CurrencyType.AUD
    case "en-GB":
    default:
      return CurrencyType.GBP
  }
}

const isSamlUser = (): boolean => {
  return isSamlUserHelper(user())
}

const isLoggedIn = (): boolean => {
  try {
    AuthService.get()
    return true
  } catch (e) {
    return false
  }
}

const isGlobalAdmin = (): boolean => {
  try {
    return isGlobalAdminHelper(business(), user())
  } catch (e) {
    return false
  }
}

const hasAdminAccess = (): boolean => {
  return isGlobalAdmin()
}

const isUserGhost = (): boolean => {
  return apps("ghost.isGhost") || false
}

const authentication = {
  // user related
  user,
  userLocale,
  userPaymentProvider,
  userProducts,
  business,
  teamAssignments,
  unAuthedBusiness,
  userUnits,
  defaultUserCurrency,
  isGlobalAdmin,
  dealPackage,

  // meta/unsure
  isLoggedIn,
  isSamlUser,
  isUserGhost,
  upsell,
  apps,
  hasAdminAccess,

  // to be deprecated
  userTenant,
  featureFlag
}

export default authentication
